import { useId, useState } from "react"
import { EyeIcon, EyeOffIcon } from "lucide-react"
import { Input } from "@workspace/web-ui/components/input"
import { Label } from "@workspace/web-ui/components/label"
import { cn } from "@workspace/web-ui/lib/utils"
import { forwardRef } from "react"

export interface PasswordInputProps
    extends React.InputHTMLAttributes<HTMLInputElement> {
    label?: string
    error?: boolean
    description?: string
    showIcon?: React.ReactNode
    hideIcon?: React.ReactNode
}

const PasswordInput = forwardRef<HTMLInputElement, PasswordInputProps>(
    ({ className, label, error, description, showIcon, hideIcon, ...props }, ref) => {
        const id = useId()
        const [isVisible, setIsVisible] = useState<boolean>(false)

        const toggleVisibility = () => setIsVisible((prevState) => !prevState)

        return (
            <div className="*:not-first:mt-2">
                {label && <Label htmlFor={id}>{label}</Label>}
                <div className="relative">
                    <Input
                        id={id}
                        ref={ref}
                        className={cn("pe-9", className)}
                        {...props}
                        type={isVisible ? "text" : "password"}
                    />
                    <button
                        className="text-muted-foreground/80 hover:text-foreground focus-visible:border-ring focus-visible:ring-ring/50 absolute inset-y-0 end-0 flex h-full w-9 items-center justify-center rounded-e-md transition-[color,box-shadow] outline-none focus:z-10 focus-visible:ring-[3px] disabled:pointer-events-none disabled:cursor-not-allowed disabled:opacity-50"
                        type="button"
                        onClick={toggleVisibility}
                        aria-label={isVisible ? "Hide password" : "Show password"}
                        aria-pressed={isVisible}
                        aria-controls={id}
                    >
                        {isVisible ? (
                            hideIcon || <EyeOffIcon size={16} aria-hidden="true" />
                        ) : (
                            showIcon || <EyeIcon size={16} aria-hidden="true" />
                        )}
                    </button>
                </div>
                {description && (
                    <p className={cn("text-sm text-muted-foreground", error && "text-destructive")}>
                        {description}
                    </p>
                )}
            </div>
        )
    }
)

PasswordInput.displayName = "PasswordInput"

export { PasswordInput }
